import React, { useEffect, useRef } from 'react';

/**
 * BioNeuralBackground — ambient "federated infrastructure" hero background.
 *
 * A faint blueprint grid. Distributed client nodes sit on grid intersections
 * and periodically route slow update pulses ALONG the grid lines toward a few
 * aggregation hubs, which flare softly as updates arrive. It evokes a
 * distributed network feeding central aggregators — calm, architectural, and
 * intentionally unobtrusive so it never competes with the hero copy.
 *
 * Single rAF loop, DPR-aware, fully gated by prefers-reduced-motion (static
 * frame, no loop). Keeps the original export name + `transparent` prop so the
 * homepage import contract is unchanged.
 */
const BioNeuralBackground = ({ transparent = false }) => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ── Palette ──
    const GRID = { r: 125, g: 211, b: 252 }; // sky-300
    const NODE = { r: 148, g: 197, b: 235 };
    const PULSE = { r: 165, g: 243, b: 252 }; // cyan-200
    const HUB = { r: 125, g: 211, b: 252 };

    let w = 0;
    let h = 0;
    let dpr = 1;
    const GAP = 58; // grid cell size (px)
    let cols = 0;
    let rows = 0;
    let ox = 0;
    let oy = 0;
    let nodes = [];
    let hubs = [];
    let pulses = [];

    const cellX = (c) => ox + c * GAP;
    const cellY = (r) => oy + r * GAP;
    const rgba = (col, a) => `rgba(${col.r}, ${col.g}, ${col.b}, ${a})`;

    const buildScene = () => {
      cols = Math.ceil(w / GAP) + 1;
      rows = Math.ceil(h / GAP) + 1;
      ox = (w - (cols - 1) * GAP) / 2;
      oy = (h - (rows - 1) * GAP) / 2;

      // pick a few aggregation hubs on interior intersections, spread out
      hubs = [];
      const hubSpots = [
        [Math.round(cols * 0.5), Math.round(rows * 0.42)],
        [Math.round(cols * 0.22), Math.round(rows * 0.72)],
        [Math.round(cols * 0.8), Math.round(rows * 0.3)],
      ];
      hubSpots.forEach(([c, r]) => {
        if (c > 0 && c < cols - 1 && r > 0 && r < rows - 1) {
          hubs.push({ c, r, flash: 0 });
        }
      });
      if (hubs.length === 0) hubs.push({ c: Math.round(cols / 2), r: Math.round(rows / 2), flash: 0 });

      // scatter client nodes on interior intersections (avoid hub cells)
      nodes = [];
      const target = Math.min(22, Math.floor((cols * rows) * 0.05));
      let guard = 0;
      const taken = new Set(hubs.map((hb) => `${hb.c},${hb.r}`));
      while (nodes.length < target && guard < 400) {
        guard += 1;
        const c = 1 + Math.floor(Math.random() * (cols - 2));
        const r = 1 + Math.floor(Math.random() * (rows - 2));
        const key = `${c},${r}`;
        if (taken.has(key)) continue;
        taken.add(key);
        nodes.push({ c, r, phase: Math.random() * Math.PI * 2, emitAt: Math.random() * 4000 });
      }
      pulses = [];
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildScene();
    };

    const onMouse = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY, active: true };
    };
    const onLeave = () => {
      mouseRef.current.active = false;
    };

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMouse);
    window.addEventListener('mouseleave', onLeave);
    resize();

    // Spawn a pulse from a node, routed Manhattan-style to the nearest hub.
    const spawnPulse = () => {
      if (nodes.length === 0 || hubs.length === 0) return;
      if (pulses.length > 14) return;
      const n = nodes[Math.floor(Math.random() * nodes.length)];
      let hub = hubs[0];
      let best = Infinity;
      hubs.forEach((hb) => {
        const d = Math.abs(hb.c - n.c) + Math.abs(hb.r - n.r);
        if (d < best) {
          best = d;
          hub = hb;
        }
      });
      if (best === 0) return;
      const horizFirst = Math.random() > 0.5;
      const seg1 = horizFirst ? Math.abs(hub.c - n.c) * GAP : Math.abs(hub.r - n.r) * GAP;
      const seg2 = horizFirst ? Math.abs(hub.r - n.r) * GAP : Math.abs(hub.c - n.c) * GAP;
      pulses.push({
        n,
        hub,
        horizFirst,
        seg1,
        seg2,
        len: seg1 + seg2,
        d: 0,
        speed: 0.05 + Math.random() * 0.02, // px per ms — slow
      });
    };

    const par = { x: 0, y: 0 };
    let lastSpawn = 0;
    let prev = performance.now();
    let raf;

    const paintBackground = () => {
      if (transparent) {
        ctx.clearRect(0, 0, w, h);
        return;
      }
      ctx.fillStyle = '#060d1a';
      ctx.fillRect(0, 0, w, h);
    };

    const drawGrid = () => {
      // minor lines
      ctx.lineWidth = 1;
      ctx.strokeStyle = rgba(GRID, 0.035);
      ctx.beginPath();
      for (let c = 0; c < cols; c += 1) {
        const x = Math.round(cellX(c)) + 0.5;
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
      }
      for (let r = 0; r < rows; r += 1) {
        const y = Math.round(cellY(r)) + 0.5;
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
      }
      ctx.stroke();
      // major lines every 4 cells (blueprint emphasis)
      ctx.strokeStyle = rgba(GRID, 0.06);
      ctx.beginPath();
      for (let c = 0; c < cols; c += 4) {
        const x = Math.round(cellX(c)) + 0.5;
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
      }
      for (let r = 0; r < rows; r += 4) {
        const y = Math.round(cellY(r)) + 0.5;
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
      }
      ctx.stroke();
    };

    const pulsePos = (p) => {
      const sx = cellX(p.n.c);
      const sy = cellY(p.n.r);
      const hx = cellX(p.hub.c);
      const hy = cellY(p.hub.r);
      if (p.horizFirst) {
        if (p.d <= p.seg1) return { x: sx + Math.sign(hx - sx) * p.d, y: sy };
        return { x: hx, y: sy + Math.sign(hy - sy) * (p.d - p.seg1) };
      }
      if (p.d <= p.seg1) return { x: sx, y: sy + Math.sign(hy - sy) * p.d };
      return { x: sx + Math.sign(hx - sx) * (p.d - p.seg1), y: hy };
    };

    const drawFrame = (time) => {
      const dt = Math.min(48, time - prev);
      prev = time;

      paintBackground();
      drawGrid();

      // parallax ease
      const m = mouseRef.current;
      const tx = m.active ? (m.x - w / 2) / w : 0;
      const ty = m.active ? (m.y - h / 2) / h : 0;
      par.x += (tx - par.x) * 0.04;
      par.y += (ty - par.y) * 0.04;
      const pxShift = par.x * 10;
      const pyShift = par.y * 10;

      // spawn pulses on a slow cadence
      if (!reduceMotion) {
        lastSpawn += dt;
        if (lastSpawn > 620) {
          lastSpawn = 0;
          spawnPulse();
        }
      }

      // ── nodes (subtle breathing) ──
      nodes.forEach((n) => {
        const x = cellX(n.c) + pxShift;
        const y = cellY(n.r) + pyShift;
        const breathe = reduceMotion ? 0.4 : 0.28 + Math.sin(time * 0.0011 + n.phase) * 0.12;
        ctx.fillStyle = rgba(NODE, breathe);
        ctx.fillRect(x - 1.6, y - 1.6, 3.2, 3.2);
      });

      // ── pulses travelling along the grid ──
      if (!reduceMotion) {
        pulses.forEach((p) => {
          p.d += p.speed * dt;
          if (p.d >= p.len) {
            p.hub.flash = 1;
            return;
          }
          const pos = pulsePos(p);
          const x = pos.x + pxShift;
          const y = pos.y + pyShift;
          // soft trailing glow
          ctx.fillStyle = rgba(PULSE, 0.1);
          ctx.beginPath();
          ctx.arc(x, y, 3.4, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = rgba(PULSE, 0.85);
          ctx.beginPath();
          ctx.arc(x, y, 1.4, 0, Math.PI * 2);
          ctx.fill();
        });
        pulses = pulses.filter((p) => p.d < p.len);
      }

      // ── hubs (aggregators) ──
      hubs.forEach((hb) => {
        const x = cellX(hb.c) + pxShift;
        const y = cellY(hb.r) + pyShift;
        if (hb.flash > 0) hb.flash = Math.max(0, hb.flash - dt / 700);
        const glow = 9 + hb.flash * 7;
        const g = ctx.createRadialGradient(x, y, 0, x, y, glow);
        g.addColorStop(0, rgba(HUB, 0.28 + hb.flash * 0.3));
        g.addColorStop(1, 'transparent');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, glow, 0, Math.PI * 2);
        ctx.fill();
        // hub mark — small square + ring
        ctx.fillStyle = rgba(PULSE, 0.7);
        ctx.fillRect(x - 2.4, y - 2.4, 4.8, 4.8);
        ctx.strokeStyle = rgba(HUB, 0.35 + hb.flash * 0.3);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(x, y, 6.5, 0, Math.PI * 2);
        ctx.stroke();
      });

      if (!reduceMotion) raf = requestAnimationFrame(drawFrame);
    };

    if (reduceMotion) {
      // seed a couple of mid-flight pulses so the static frame isn't empty
      spawnPulse();
      drawFrame(performance.now());
    } else {
      raf = requestAnimationFrame(drawFrame);
    }

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouse);
      window.removeEventListener('mouseleave', onLeave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [transparent]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
};

export default BioNeuralBackground;
